import { useEffect, useRef, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import {
  startAnalyticsSession,
  endAnalyticsSession,
  endAnalyticsSessionOnUnload,
} from '@/features/analytics/services/analytics-session.service'
import {
  enqueueEvent,
  flushOnUnload,
} from '@/features/analytics/services/analytics-tracker.service'

export interface AnalyticsProviderProps {
  children: ReactNode
}

// O 1º segmento da rota é a feature-slice (ex: /goals/new -> "goals", /agendas/123 -> "agendas").
function deriveFeatureFromPathname(pathname: string): string | undefined {
  return pathname.split('/').filter(Boolean)[0]
}

// Evita payload excessivo em stack traces.
const MAX_STACK_LENGTH = 1000

function truncateStack(stack: string | undefined): string | undefined {
  if (!stack) return undefined
  return stack.length > MAX_STACK_LENGTH ? stack.slice(0, MAX_STACK_LENGTH) : stack
}

// unhandledrejection.reason pode ser qualquer valor (Error, string, objeto, undefined) — converte
// para string de forma segura, sem lançar em caso de objeto circular ou toString customizado.
function stringifyRejectionReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message
  if (typeof reason === 'string') return reason
  try {
    return JSON.stringify(reason) ?? String(reason)
  } catch {
    return String(reason)
  }
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const location = useLocation()
  // Sobrevive ao double-invoke de efeitos do StrictMode (mesma instância de componente),
  // evitando um start() duplicado — só é resetada numa transição real para "não autenticado".
  const hasActiveSessionRef = useRef(false)
  // Evita screen.view duplicado no double-invoke do StrictMode para o mesmo pathname;
  // resetada no logout para não "esconder" um screen.view legítimo num login seguinte na mesma rota.
  const lastTrackedPathnameRef = useRef<string | null>(null)

  useEffect(() => {
    if (isAuthenticated) {
      if (hasActiveSessionRef.current) return
      hasActiveSessionRef.current = true
      void startAnalyticsSession()
      return
    }

    if (hasActiveSessionRef.current) {
      hasActiveSessionRef.current = false
      void endAnalyticsSession()
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) {
      lastTrackedPathnameRef.current = null
      return
    }

    if (lastTrackedPathnameRef.current === location.pathname) return
    lastTrackedPathnameRef.current = location.pathname

    enqueueEvent('screen.view', {
      screen: location.pathname,
      feature: deriveFeatureFromPathname(location.pathname),
    })
  }, [isAuthenticated, location.pathname])

  // Registrados uma única vez (independente de isAuthenticated no momento do registro) — os
  // handlers e as funções *OnUnload chamadas são no-op seguro se não houver sessão/fila ativa.
  useEffect(() => {
    function handleBeforeUnload(): void {
      endAnalyticsSessionOnUnload()
    }

    // visibilitychange "hidden" é mais confiável que beforeunload em mobile/alguns browsers, mas
    // só descarrega eventos — a aba pode voltar a ficar visível, então não encerra a sessão aqui.
    function handleVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        flushOnUnload()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Sem guard de isAuthenticated para não perder erros durante o carregamento/transição de auth; nunca chama preventDefault() (estritamente adicional ao Sentry).
  useEffect(() => {
    function handleWindowError(event: ErrorEvent): void {
      enqueueEvent('error.uncaught', {
        metadata: {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: truncateStack(event.error instanceof Error ? event.error.stack : undefined),
        },
      })
    }

    function handleUnhandledRejection(event: PromiseRejectionEvent): void {
      enqueueEvent('error.uncaught', {
        metadata: {
          reason: stringifyRejectionReason(event.reason),
          stack: truncateStack(event.reason instanceof Error ? event.reason.stack : undefined),
        },
      })
    }

    window.addEventListener('error', handleWindowError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleWindowError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return <>{children}</>
}
